import { TopPage } from "@/components/elements/TopPage";
import { ExamForm } from "@/features/learning-content/components/ExamForm/ExamForm";
import { useExamDetail } from "@/features/learning-content/hooks/useExamDetail";
import { useIndustryDetail } from "@/features/learning-content/hooks/useIndustryDetail";
import { convertExamDetailToForm } from "@/features/learning-content/utils/converts";
import { TExamCreateForm } from "@/features/learning-content/types/learningContent";
import { dayjs } from "@/utils/dayjs";
import { CircularProgress, Stack, Typography, Box } from "@mui/material";
import { styled } from "@mui/system";
import { FC, useMemo, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";

type TExamCreatePageProps = {};

export const ExamCreatePage: FC<TExamCreatePageProps> = () => {
  const { id } = useParams();
  const location = useLocation();
  const formRef = useRef<{ submit: () => void }>(null);
  const { data: industryData } = useIndustryDetail(id);

  // If navigated with a copyFromExamId, fetch its detail to pre-populate the form
  const copyFromExamId = (location.state as { copyFromExamId?: number })?.copyFromExamId;
  const { data: sourceExam, isLoading: isLoadingSource } = useExamDetail(copyFromExamId?.toString());

  const initialData = useMemo<TExamCreateForm | undefined>(() => {
    if (!sourceExam) return undefined;
    const formData = convertExamDetailToForm(sourceExam);
    // Reset status and examMonth for new entries
    return { ...formData, status: "active", examMonth: sourceExam.examMethod === "cbt" ? null : dayjs().startOf("month") };
  }, [sourceExam]);

  // Show loading while fetching source exam data
  if (copyFromExamId && isLoadingSource) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PageWrapper gap={4}>
      <TopPage
        title={
          <Typography variant="head32Bold" display="flex" alignItems="center" gap={2}>
            教材と試験情報の管理 - 新しい試験情報の登録
          </Typography>
        }
        description={
          <Typography variant="body14">
            新しい試験情報を登録できます。ペーパーテスト実施の分野において、CBTで一度登録するとペーパーテストを登録できなくなるためご注意ください。
          </Typography>
        }
        enablePageBack={false}
      />
      <ExamForm ref={formRef} industryName={industryData?.name} initialData={initialData} disableExamMethod={sourceExam?.examMethod === "cbt"} />
    </PageWrapper>
  );
};

const PageWrapper = styled(Stack)`
  width: 1198px;
  margin: 0 auto;
  margin-top: 8px;
`;
